/**
 * Printer Lookup Utilities
 * Reused from silent-printer's connection.ts logic
 * Builds item_printer_lookup and splits items by printer/cut settings
 */

export type ItemPrinterConfig = {
  api_key: string;
  is_kitchen_slip: boolean;
  kitchenSlipWithPrice?: boolean;
  auto_print: boolean;
  cut_by_item: boolean;
  cut_by_cat: boolean;
  waiter_slip: boolean;
  waiter_slip_cut: string;
  table_num_on_item: boolean;
  table_num_placement: string;
  auto_print_after_confirmation: boolean;
  cup_label: boolean;
  cup_label_paper: string;
  bag_label: boolean;
  delivery_slip: boolean;
};

export type ItemPrinterLookup = Record<string, ItemPrinterConfig[]>;

/**
 * Build item_printer_lookup from store's printerSettings
 * Reused from silent-printer's handleStore function
 * 
 * NOTE: This handles items from multiple menus correctly because:
 * - Item IDs are globally unique (not per-menu)
 * - The lookup uses item.id as the key, which works regardless of which menu the item belongs to
 * - Items from different menus can be selected in the same virtual printer configuration
 */
export function checkedItemId(item: any): string {
  return String(item?.id || "").trim();
}

export function slimCheckedItems(items: any[]): Array<{ id: string }> {
  const seen = new Set<string>();
  const refs: Array<{ id: string }> = [];
  (items || []).forEach((item) => {
    const id = checkedItemId(item);
    if (!id || seen.has(id)) return;
    seen.add(id);
    refs.push({ id });
  });
  return refs;
}

export function slimPrinterSettings(printerSettings: any[]): any[] {
  return (printerSettings || []).map((printer) => {
    if (!printer?.itemsTab || !Array.isArray(printer.itemsTab.checkedItems)) return printer;
    return {
      ...printer,
      itemsTab: {
        ...printer.itemsTab,
        checkedItems: slimCheckedItems(printer.itemsTab.checkedItems),
      },
    };
  });
}

export function printerApiKeyOf(printer: any): string {
  return String(printer?.generalTab?.apikey || "").trim();
}

function configForPrinter(entry: any): ItemPrinterConfig {
  return {
    api_key: entry.generalTab.apikey,
    auto_print: entry.generalTab.autoPrint,
    is_kitchen_slip: Boolean(entry.itemsTab?.kitchenSlip),
    kitchenSlipWithPrice: Boolean(entry.itemsTab?.kitchenSlipWithPrice),
    cut_by_item: Boolean(entry.itemsTab?.cutbyItem),
    cut_by_cat: Boolean(entry.itemsTab?.cutbyCategory),
    waiter_slip: Boolean(entry.itemsTab?.waiterSlip),
    waiter_slip_cut: entry.itemsTab?.waiterSlipCut === 'seat' ? 'seat' : 'table',
    table_num_on_item: Boolean(entry.itemsTab?.tableNumOnItem),
    table_num_placement: ['beginning', 'above', 'below', 'end'].includes(entry.itemsTab?.tableNumPlacement) ? entry.itemsTab.tableNumPlacement : 'end',
    auto_print_after_confirmation: Boolean(entry.generalTab?.autoPrintAfterConfirmation),
    cup_label: Boolean(entry.itemsTab?.cupLabel),
    cup_label_paper: entry.itemsTab?.cupLabelPaper === 'linerless' ? 'linerless' : 'receipt',
    bag_label: Boolean(entry.itemsTab?.bagLabel),
    delivery_slip: Boolean(entry.itemsTab?.deliverySlip),
  };
}

export function printerKeysByItemId(printerSettings: any[]): Map<string, string[]> {
  const keys = new Map<string, Set<string>>();
  (printerSettings || []).forEach((printer) => {
    const apiKey = printerApiKeyOf(printer);
    if (!apiKey) return;
    (printer?.itemsTab?.checkedItems || []).forEach((item: any) => {
      const id = checkedItemId(item);
      if (!id) return;
      if (!keys.has(id)) keys.set(id, new Set());
      keys.get(id)!.add(apiKey);
    });
  });
  const membership = new Map<string, string[]>();
  keys.forEach((apiKeys, id) => membership.set(id, Array.from(apiKeys)));
  return membership;
}

export function printerKeyUpdates(
  previousSettings: any[],
  nextSettings: any[],
  printerId: string
): Map<string, string[]> {
  const affected = new Set<string>();
  const collect = (settings: any[]) => {
    const printer = (settings || []).find((entry) => entry?.id === printerId);
    (printer?.itemsTab?.checkedItems || []).forEach((item: any) => {
      const id = checkedItemId(item);
      if (id) affected.add(id);
    });
  };
  collect(previousSettings);
  collect(nextSettings);
  const membership = printerKeysByItemId(nextSettings);
  const updates = new Map<string, string[]>();
  affected.forEach((id) => updates.set(id, membership.get(id) || []));
  return updates;
}

export function patchStoreItems(items: any[], updates: Map<string, string[]>): any[] | null {
  if (!Array.isArray(items) || !updates?.size) return null;
  let changed = false;
  const next = items.map((item) => {
    if (!item?.id || !updates.has(item.id)) return item;
    changed = true;
    return { ...item, printerApiKeys: updates.get(item.id) };
  });
  return changed ? next : null;
}

export const KITCHEN_ALL_API_KEY = "KITCHEN-ALL";

export function isKitchenAllPrinter(entry: any): boolean {
  return printerApiKeyOf(entry) === KITCHEN_ALL_API_KEY || entry?.id === "kitchen-all";
}

function kitchenAllConfigs(printerSettings: any[]): ItemPrinterConfig[] {
  return (printerSettings || []).filter((entry) => {
    if (!isKitchenAllPrinter(entry)) return false;
    if (entry?.generalTab?.disable === true) return false;
    if (entry?.generalTab?.autoPrint === false) return false;
    return true;
  }).map((entry) => configForPrinter({
    ...entry,
    generalTab: {
      ...entry.generalTab,
      apikey: printerApiKeyOf(entry) || KITCHEN_ALL_API_KEY,
      autoPrint: true,
    },
    itemsTab: {
      ...entry.itemsTab,
      kitchenSlip: true,
    },
  }));
}

function attachKitchenAll(lookup: ItemPrinterLookup, id: string, configs: ItemPrinterConfig[]) {
  if (!id || !configs.length) return;
  const current = lookup[id] ? [...lookup[id]] : [];
  configs.forEach((config) => {
    if (!current.some((entry) => entry.api_key === config.api_key)) current.push(config);
  });
  lookup[id] = current;
}

export function buildItemPrinterLookup(printerSettings: any[], cartItems?: any[]): ItemPrinterLookup {
  const itemPrinterLookup: ItemPrinterLookup = {};
  const configsByKey: Record<string, ItemPrinterConfig[]> = {};

  printerSettings.forEach((entry) => {
    if (isKitchenAllPrinter(entry)) return;
    if (entry?.generalTab?.disable === true) return;
    const printerConfig = configForPrinter(entry);
    const apiKey = printerApiKeyOf(entry);
    if (apiKey) {
      if (!configsByKey[apiKey]) configsByKey[apiKey] = [];
      configsByKey[apiKey].push(printerConfig);
    }

    const checkedItems = entry?.itemsTab?.checkedItems || [];
    checkedItems.forEach((item: any) => {
      const id = checkedItemId(item);
      if (!id) return;
      if (!itemPrinterLookup[id]) {
        itemPrinterLookup[id] = [];
      }
      itemPrinterLookup[id].push(printerConfig);
    });
  });

  (cartItems || []).forEach((item) => {
    if (!Array.isArray(item?.printerApiKeys)) return;
    const id = checkedItemId(item);
    if (!id) return;
    const configs: ItemPrinterConfig[] = [];
    const seen = new Set<string>();
    item.printerApiKeys.forEach((key: any) => {
      const apiKey = String(key || "").trim();
      if (!apiKey || seen.has(apiKey)) return;
      seen.add(apiKey);
      (configsByKey[apiKey] || []).forEach((config) => configs.push(config));
    });
    itemPrinterLookup[id] = configs;
  });

  const kitchenAll = kitchenAllConfigs(printerSettings);
  (cartItems || []).forEach((item) => attachKitchenAll(itemPrinterLookup, checkedItemId(item), kitchenAll));

  return itemPrinterLookup;
}

// NOTE: filterItemPrinterLookupByPhysicalPrinters function removed
// Print job creation now loops by physical printers first (correct flow)
// See POSInterface.js handlePrintCartItems for the corrected implementation

/**
 * Split cart items by printer and cut settings
 * Reused from silent-printer's handleOrder function
 * 
 * NOTE: This handles items from multiple menus correctly because:
 * - Item IDs are globally unique, so lookup by item.id works across menus
 * - Cut-by-category groups by item.category, which can span multiple menus
 * - Cut-by-item creates separate receipts per item, regardless of menu
 */
export function splitItemsByPrinter(
  items: any[],
  itemPrinterLookup: ItemPrinterLookup,
  storeData: any
): Array<{
  items: any[];
  printerQuery: { api_key: string };
  isKitchenSlip: boolean;
  kitchenSlipWithPrice: boolean;
  cutbyItem: boolean;
  cutbyCategory: boolean;
  storeData: any;
}> {
  const splitOrders: Record<string, Record<string, any>> = {};

  items.forEach((item) => {
    // Lookup works for items from any menu since item IDs are globally unique
    const printers = itemPrinterLookup[item.id];
    if (printers && printers.length) {
      printers.forEach((printer) => {
        if (!printer.auto_print) {
          return;
        }
        
        if (!splitOrders[printer.api_key]) {
          splitOrders[printer.api_key] = {};
        }
        
        let orderKey: string;
        if (printer.cut_by_item) {
          orderKey = item.id;
        } else if (printer.cut_by_cat) {
          orderKey = item.category || 'uncategorized';
        } else {
          orderKey = 'default';
        }
        
        if (!splitOrders[printer.api_key][orderKey]) {
          splitOrders[printer.api_key][orderKey] = {
            items: [],
            isKitchenSlip: printer.is_kitchen_slip,
            kitchenSlipWithPrice: printer.kitchenSlipWithPrice || false,
            cutbyItem: printer.cut_by_item,
            cutbyCategory: printer.cut_by_cat,
          };
        }
        
        splitOrders[printer.api_key][orderKey].items.push(item);
      });
    }
  });

  // Convert splitOrders to array of print jobs
  const printJobs: Array<{
    items: any[];
    printerQuery: { api_key: string };
    isKitchenSlip: boolean;
    kitchenSlipWithPrice: boolean;
    cutbyItem: boolean;
    cutbyCategory: boolean;
    storeData: any;
  }> = [];

  for (const apiKey in splitOrders) {
    for (const orderKey in splitOrders[apiKey]) {
      const splitOrder = splitOrders[apiKey][orderKey];
      
      if (splitOrder.items.length > 0) {
        const printJob = {
          items: splitOrder.items,
          printerQuery: { api_key: apiKey },
          isKitchenSlip: splitOrder.isKitchenSlip,
          kitchenSlipWithPrice: splitOrder.kitchenSlipWithPrice,
          cutbyItem: splitOrder.cutbyItem,
          cutbyCategory: splitOrder.cutbyCategory,
          storeData,
        };
        printJobs.push(printJob);
      }
    }
  }

  return printJobs;
}

export {};

